import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  Table,
  TableRow,
  useTheme,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import {
  deleteUser,
  unarchiveUser,
  multipleUsersDelete,
} from 'src/services/user.service';

import TableLoader from 'src/components/loaders/Loader';
import Scrollbar from 'src/components/scrollbar/Scrollbar';
import TableToolbar from 'src/components/ToolBars/TableToolbar';
import TableHeaders from 'src/components/tableHeaders/TableHeaders';
import TablePaginations from 'src/components/table-pagination/TablePaginations';

import UsersTableBody from './tableBody';
import FilterPopover from '../popovers/filterPopover';
import OptionsPopover from '../popovers/optionsPopover';
import { TABLE_HEAD_ADMIN, TABLE_HEAD_DEPMANAGER } from '../tableHeads';

export default function UsersTable({
  filterName,
  setFilterName,
  searchBy,
  setSearchBy,
  filterBy,
  setFilterBy,
  filterValue,
  setFilterValue,
  fullFilteredData,
  role,
  setRowsPerPage,
  selected,
  setSelected,
  order,
  setOrder,
  setOrderBy,
  orderBy,
  rowsPerPage,
  page,
  setPage,
  title,
  departmentsData,
  setSelectedRow,
  selectedRow,
  setModifyUserModal,
  fetchData,
  isLoading,
  activeTab,
  usersData,
  onEditPermissions,
  userCapabilities,
  onArchive,
  onRestore,
}) {
  const [open, setOpen] = useState(null);
  const [selectedId, setSelectedId] = useState([]);
  const [showDelete, setShowDelete] = useState(true);

  // States to control the filter popover opening and nested selections
  const [filterPopover, setFilterPopover] = useState(null);
  const [subFilterPopover, setSubFilterPopover] = useState({
    department: null,
    status: null,
    role: null,
  });

  const handleOpenFilter = (event) => {
    setFilterPopover(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterPopover(null);
  };

  const handleCloseSubFilter = () => {
    setSubFilterPopover({
      department: null,
      status: null,
      role: null,
    });
  };

  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const auth = useAuth();
  const { showToast, hideToast } = useToast();

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { canUpdateUser, canDeleteUser, canManagePermissions } = userCapabilities;

  const hasAnyAction = useMemo(
    () => !!(canUpdateUser || canDeleteUser || canManagePermissions),
    [canUpdateUser, canDeleteUser, canManagePermissions]
  );

  const totalColumns = (activeTab === 0 ? 8 : 7) + (hasAnyAction ? 1 : 0);

  const handleOpenMenu = (event, row) => {
    event.stopPropagation();
    setOpen(event.currentTarget);
    setSelectedRow(row);
    setShowDelete(row.id !== auth?.id && canDeleteUser);
  };

  const handleCloseMenu = () => setOpen(null);

  const filteredData = useMemo(
    () =>
      activeTab === 0
        ? fullFilteredData.filter((u) => !u.archived)
        : fullFilteredData.filter((u) => u.archived),
    [activeTab, fullFilteredData]
  );
  const currentPageData = useMemo(
    () => filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  const isDeptManager = auth?.permissions?.includes('DASHBOARD:DEPT_MANAGER');
  const headLabelSource = isDeptManager ? TABLE_HEAD_DEPMANAGER : TABLE_HEAD_ADMIN;
  const resolvedHeadLabels = hasAnyAction
    ? headLabelSource
    : headLabelSource.filter((h) => h.id !== '' && h.id !== 'options');

  return (
    <>
      <Card sx={{ borderRadius: '24px', border: '1px solid rgba(0,0,0,0.08)' }}>
        <TableToolbar
          numSelected={activeTab === 0 ? selected.length : 0}
          filterName={filterName} // Maps to searchValue
          setFilterName={setFilterName} // Maps to setSearchValue
          filterBy={searchBy} // Fixed: Binds your text-search reference tracking state (searchBy)
          setFilterBy={setSearchBy} // Fixed: Binds your text-search reference setter (setSearchBy)
          setPage={setPage} // Connected to reset pages on query change
          departmentsData={departmentsData} // Passed down for sub-popper listings
          handleOpenFilter={handleOpenFilter} // Connected to trigger popover opening
          handleCloseFilter={handleCloseFilter} // Connected to trigger popover closing
          filterValue={filterValue} // Passed down to display active filter Chip
          setFilterValue={setFilterValue} // Passed down to allow Chip clearing
          onFilterName={(e) => {
            setPage(0);
            setFilterName(e.target.value);
          }}
          selected={selectedId}
          handleRemoveSelected={() => {
            setSelected([]);
            setSelectedId([]);
          }}
          deleteFunc={
            canDeleteUser
              ? (ids) =>
                  genericApiToaster(
                    multipleUsersDelete,
                    [ids],
                    '',
                    t('errorMessages.couldntDeleteUser'),
                    showToast,
                    hideToast,
                    t,
                    fetchData
                  )
              : null
          }
          model={t(`nav.${title}`)}
          data={usersData}
        />
        <Scrollbar>
          <TableContainer
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(0, 0, 0, 0.15)',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: isDark
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.3)',
                },
              },
            }}
          >
            <Table size={isSmallScreen ? 'small' : 'medium'}>
              <TableHeaders
                order={order}
                orderBy={orderBy}
                headLabel={resolvedHeadLabels}
                showCheckBox={activeTab === 0}
                rowCount={filteredData.length}
                numSelected={selected.length}
                onRequestSort={(e, p) => {
                  setOrder(orderBy === p && order === 'asc' ? 'desc' : 'asc');
                  setOrderBy(p);
                }}
                onSelectAllClick={() => {
                  const allEmails = currentPageData.map((n) => n.email);
                  const allIds = currentPageData.map((n) => n.id);
                  if (allIds.every((id) => selectedId.includes(id))) {
                    setSelectedId(selectedId.filter((id) => !allIds.includes(id)));
                    setSelected(selected.filter((email) => !allEmails.includes(email)));
                  } else {
                    setSelected([...new Set([...selected, ...allEmails])]);
                    setSelectedId([...new Set([...selectedId, ...allIds])]);
                  }
                }}
              />
              {isLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={totalColumns} sx={{ textAlign: 'center' }}>
                      <TableLoader size={100} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <UsersTableBody
                  currentPageData={currentPageData}
                  departmentsData={departmentsData}
                  handleClick={(e, email, id) => {
                    const selIdx = selected.indexOf(email);
                    if (selIdx === -1) {
                      setSelected([...selected, email]);
                      setSelectedId([...selectedId, id]);
                    } else {
                      setSelected(selected.filter((x) => x !== email));
                      setSelectedId(selectedId.filter((x) => x !== id));
                    }
                  }}
                  selected={selected}
                  handleOpenMenu={handleOpenMenu}
                  handleClickRow={(r) =>
                    canUpdateUser && (setSelectedRow(r), setModifyUserModal(true))
                  }
                  activeTab={activeTab}
                  hasAnyAction={hasAnyAction}
                />
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <TablePaginations
          filteredEntity={filteredData}
          handleChangePage={(e, n) => setPage(n)}
          handleChangeRowsPerPage={(e) => {
            setPage(0);
            setRowsPerPage(parseInt(e.target.value, 10));
          }}
          page={page}
          rowsPerPage={rowsPerPage}
        />
      </Card>
      <FilterPopover
        data={usersData}
        setSubFilterPopover={setSubFilterPopover}
        subFilterPopover={subFilterPopover}
        handleCloseSubFilter={handleCloseSubFilter}
        setFilterBy={setFilterBy} // Category filter state
        setFilterName={setFilterValue} // Category filter value state (maps selected option directly to filterValue)
        setSearchBy={setSearchBy}
        setPage={setPage}
        departmentsData={departmentsData}
        handleCloseFilter={handleCloseFilter}
        filterPopover={filterPopover}
      />
      {hasAnyAction && (
        <OptionsPopover
          open={open}
          handleCloseMenu={handleCloseMenu}
          setModifyUserModal={setModifyUserModal}
          selectedRow={selectedRow}
          removeUser={(id, arc) =>
            genericApiToaster(
              deleteUser,
              id,
              arc
                ? t('successMessages.successUserArchived')
                : t('successMessages.successUserDelete'),
              t('errorMessages.couldntDeleteUser'),
              showToast,
              hideToast,
              t,
              fetchData
            )
          }
          showDelete={showDelete}
          unarchive={(id) =>
            genericApiToaster(
              unarchiveUser,
              id,
              t('successMessages.successUserUnarchive'),
              t('errorMessages.couldntUnarchiveUser'),
              showToast,
              hideToast,
              t,
              fetchData
            )
          }
          onEditPermissions={onEditPermissions}
          userCapabilities={userCapabilities}
          onArchive={onArchive}
          onRestore={onRestore}
        />
      )}
    </>
  );
}

UsersTable.propTypes = {
  fullFilteredData: PropTypes.array,
  role: PropTypes.string,
  selected: PropTypes.array,
  setSelected: PropTypes.func,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  setOrder: PropTypes.func,
  setOrderBy: PropTypes.func,
  rowsPerPage: PropTypes.number,
  page: PropTypes.number,
  filterName: PropTypes.string,
  searchBy: PropTypes.string,
  setSearchBy: PropTypes.func,
  filterBy: PropTypes.string,
  filterValue: PropTypes.string,
  setFilterValue: PropTypes.func,
  setPage: PropTypes.func,
  title: PropTypes.string,
  departmentsData: PropTypes.array,
  setRowsPerPage: PropTypes.func,
  setSelectedRow: PropTypes.func,
  selectedRow: PropTypes.object,
  setFilterName: PropTypes.func,
  setModifyUserModal: PropTypes.func,
  setFilterBy: PropTypes.func,
  fetchData: PropTypes.func,
  isLoading: PropTypes.bool,
  activeTab: PropTypes.number,
  usersData: PropTypes.array,
  onEditPermissions: PropTypes.func,
  userCapabilities: PropTypes.object.isRequired,
  onArchive: PropTypes.func,
  onRestore: PropTypes.func,
};
